import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Building2,
  Construction,
  Star,
  Settings,
  LayoutGrid,
} from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/utils';

import { OrganizationDetails } from './organization/OrganizationDetails';
import { ProjectTypeSettings } from './project/ProjectTypeSettings';
import { SkillSettings } from './skills/SkillSettings';

const SIDEBAR_ITEMS = [
  {
    value: 'organization',
    label: 'Organization',
    icon: Building2,
    description: 'Branding & contacts',
  },
  {
    value: 'project',
    label: 'Project Config',
    icon: Construction,
    description: 'Workflows & types',
  },
  {
    value: 'skills',
    label: 'Skills',
    icon: Star,
    description: 'Crew and ticket capabilities',
  },
];

export const AdminSettings = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'organization';

  const onTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (!user) return <div className="p-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="w-full max-w-none py-6 px-4 md:px-8 animate-in fade-in duration-500">
      <Tabs
        value={currentTab}
        onValueChange={onTabChange}
        className="flex flex-col md:flex-row gap-8 items-start w-full"
      >
        {/* SIDEBAR */}
        <aside className="w-full md:w-64 lg:w-72 shrink-0 md:sticky md:top-6 self-start">
          <TabsList className="flex md:flex-col h-auto bg-transparent p-0 gap-1 w-full justify-start overflow-x-auto md:overflow-visible pb-2 md:pb-0">
            {SIDEBAR_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = currentTab === item.value;

              return (
                <TabsTrigger
                  key={item.value}
                  value={item.value}
                  className={cn(
                    'group flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-all w-full justify-start text-left border border-transparent cursor-pointer',
                    'text-muted-foreground hover:bg-muted hover:text-foreground',
                    'data-[state=active]:bg-primary/5 data-[state=active]:text-foreground',
                    'md:data-[state=active]:border-l-[3px] md:data-[state=active]:border-l-secondary md:data-[state=active]:rounded-l-none md:data-[state=active]:dark:border-l-secondary',
                  )}
                >
                  <Icon
                    className={cn(
                      'h-5 w-5 shrink-0 transition-colors',
                      isActive
                        ? 'text-secondary'
                        : 'text-muted-foreground group-hover:text-foreground',
                    )}
                  />

                  <div className="flex flex-col">
                    <span
                      className={cn(
                        'font-semibold leading-none mb-1',
                        isActive && 'text-foreground',
                      )}
                    >
                      {item.label}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground hidden md:inline-block leading-snug">
                      {item.description}
                    </span>
                  </div>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </aside>

        {/* MAIN CONTENT */}
        <div className="flex-1 min-w-0 w-full">
          <TabsContent
            value="organization"
            className="mt-0 focus-visible:outline-none w-full"
          >
            <OrganizationDetails />
            {/* <Card className="border-border shadow-sm w-full">
              <CardHeader className="pb-4 border-b border-border/50 mb-4 bg-muted/20">
                <CardTitle>Organization Profile</CardTitle>
                <CardDescription>
                  Company branding and contact details.
                </CardDescription>
              </CardHeader>
              <CardContent>
            
              </CardContent>
            </Card> */}
          </TabsContent>

          <TabsContent
            value="project"
            className="mt-0 focus-visible:outline-none w-full"
          >
            <Card className="border-border shadow-sm w-full">
              <CardHeader className="pb-4 border-b border-border/50 mb-4 bg-muted/20">
                <CardTitle>Project Configuration</CardTitle>
                <CardDescription>
                  Default settings for new projects.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ProjectTypeSettings />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent
            value="skills"
            className="mt-0 focus-visible:outline-none w-full"
          >
            <Card className="border-border shadow-sm w-full">
              <CardHeader className="pb-4 border-b border-border/50 mb-4 bg-muted/20">
                <CardTitle>Skills & Qualifications</CardTitle>
                <CardDescription>
                  Manage required crew capabilities.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SkillSettings />
              </CardContent>
            </Card>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
